import React from 'react';
import { Avatar } from '@mui/material';
import { Box, styled } from '@mui/system';

import * as ItemUtils from '../../common/ItemTools';
import HeatBuzzBadge from '../common/HeatBuzzBadge';
import { MEDIA_MAX_HEIGHT, MEDIA_MIN_HEIGHT } from '../common/card/styles';
import { useCardShows } from '../common/card/useCardDensity';
import { SERIF_STACK } from '../common/tokens';

// The frame's preferred shape. It holds until the clamp overrides it, so the
// body never shifts when an image's natural aspect comes in.
const FRAME_ASPECT = 16 / 9;

// Module-level cache of cover-image aspect ratios keyed by URL. Lets us
// pick the right image fit (contain / cover / square-centered) on first
// paint after re-mount or pagination, instead of hiding the image until
// onLoad fires.
const ASPECT_CACHE = new Map();

const Photo = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  flexShrink: 0,
  overflow: 'hidden',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#eeeeee',
  minHeight: MEDIA_MIN_HEIGHT,
  maxHeight: MEDIA_MAX_HEIGHT,
}));

const PhotoImg = styled('img')({
  display: 'block',
});

// Circular portrait used when the card's image is an author picture, mirroring
// the round avatar of AuthorCard. Sized off the 16:9 frame height so it stays a
// centered circle regardless of the source image's aspect ratio.
const AuthorPortrait = styled(Avatar)(({ theme }) => ({
  height: '88%',
  width: 'auto',
  aspectRatio: '1 / 1',
  maxWidth: '90%',
  fontFamily: SERIF_STACK,
  fontWeight: 'bold',
  fontSize: 'clamp(1.5rem, 14cqw, 4rem)',
  border: `3px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[2],
}));

const SiteLogo = styled(Avatar)(({ theme }) => ({
  height: '88%',
  width: 'auto',
  aspectRatio: '1 / 1',
  maxWidth: '90%',
  borderRadius: '28%',
  fontFamily: SERIF_STACK,
  fontWeight: 'bold',
  fontSize: 'clamp(1.5rem, 14cqw, 4rem)',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.background.paper,
  '& .MuiAvatar-img': {
    objectFit: 'contain',
  },
}));

const TypeChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'typecolor',
})(({ typecolor }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.4px',
  color: '#fff',
  textTransform: 'uppercase',
  padding: '4px 8px',
  borderRadius: '0 8px 0 8px',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  background: typecolor || 'gray',
  zIndex: 2,
}));

// Cover-sizing strategy buckets — every image lives in a fixed 16:9
// photo frame, so the body never shifts when an image loads.
// - portrait/square/narrow (<1.65): image capped at 90% of frame,
//   centered — empty space on all four sides.
// - near 16:9 (1.65–1.9): cover, top — minimal crop fills the small gaps.
// - wide (>1.9): contain, centered vertically — image's top/bottom bars
//   are split evenly above/below.
function coverStyle(aspect) {
  if (aspect && aspect < 1.65) {
    return {
      width: '90%', height: '90%', objectFit: 'contain', objectPosition: 'center',
    };
  }
  const objectFit = aspect && aspect <= 1.9 ? 'cover' : 'contain';
  const objectPosition = aspect && aspect > 1.9 ? 'center' : 'top center';
  return {
    width: '100%', height: '100%', objectFit, objectPosition,
  };
}

/**
 * The card's image frame, and everything that overlays it: the media-type chip
 * and the heat/buzz badge. The forwarded ref lands on the frame itself, which is
 * the card's drop target.
 *
 * The badge only becomes clickable for a moderator, on an item that carries
 * source stats. Zen mode takes it off the frame; the ladder never does, since
 * the frame always has room.
 */
const CardPhoto = React.forwardRef(({
  item, sourceLetter, titleAuthorName, metrics,
  heatScore, buzzScore,
  onItemRead, onTypeView, onExpandClick,
}, ref) => {
  const { url: imageUrl, source: imageSource } = ItemUtils.getImageInfo(item);
  const shows = useCardShows();
  const isAuthorImage = imageSource === 'author';
  const isLogoImage = imageSource === 'logo';

  const imgRef = React.useRef(null);
  const [imgAspect, setImgAspect] = React.useState(
    () => (imageUrl && ASPECT_CACHE.get(imageUrl)) || null,
  );
  React.useLayoutEffect(() => {
    if (imgAspect != null || !imageUrl) return;
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth) {
      const aspect = img.naturalWidth / img.naturalHeight;
      ASPECT_CACHE.set(imageUrl, aspect);
      setImgAspect(aspect);
    }
  }, [imageUrl, imgAspect]);

  return (
    <Photo
      ref={ref}
      onClick={() => onItemRead && onItemRead(item)}
      sx={{ aspectRatio: `${FRAME_ASPECT} / 1` }}
    >
      {imageUrl && isAuthorImage && (
        <AuthorPortrait src={imageUrl}>
          {(titleAuthorName && titleAuthorName[0] && titleAuthorName[0].toUpperCase())
            || sourceLetter || '?'}
        </AuthorPortrait>
      )}
      {imageUrl && isLogoImage && (
        <SiteLogo src={imageUrl} variant="rounded">
          {sourceLetter || '?'}
        </SiteLogo>
      )}
      {imageUrl && !isAuthorImage && !isLogoImage && (
        <PhotoImg
          ref={imgRef}
          src={imageUrl}
          alt=""
          style={{
            ...coverStyle(imgAspect),
            ...(imgAspect == null && { visibility: 'hidden' }),
          }}
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.currentTarget;
            if (naturalWidth && naturalHeight) {
              const aspect = naturalWidth / naturalHeight;
              ASPECT_CACHE.set(imageUrl, aspect);
              setImgAspect(aspect);
            }
          }}
        />
      )}
      {metrics.label && (
        <TypeChip
          typecolor={metrics.color}
          title={onTypeView ? 'Filter by type' : undefined}
          style={{ cursor: onTypeView ? 'pointer' : 'default' }}
          onClick={onTypeView ? (e) => {
            e.stopPropagation();
            onTypeView(item.type, metrics.label);
          } : undefined}
        >
          {metrics.label}
        </TypeChip>
      )}
      {shows('heatBadge') && (
        <HeatBuzzBadge
          heat={heatScore}
          buzz={buzzScore}
          onClick={item.extra?.stats ? onExpandClick : null}
        />
      )}
    </Photo>
  );
});

export default CardPhoto;
